import { Container, Graphics } from "pixi.js";
import type { MapSchema } from "@colyseus/schema";

import type { Loot } from "../../../shared/ArenaState";
import { Sea, waveHeightAt, WAVE_HEIGHT } from "./Waves";
import { wrapCoord, wrapDelta } from "../../../shared/Sailing";

/** Radius of the coin itself, in screen pixels. */
const RADIUS = 11;
/** Radius of the halo behind it. Wide enough to be spotted before the coin resolves. */
const HALO_RADIUS = 19;
const COLOR = 0xffffff;
/**
 * Coins burned out of a hull's turbo, in the palette's cream rather than white — see
 * design/UI.md. Worth exactly as much as the white ones, but a warm trail of them across the
 * water is a ship that just ran, and that is worth reading from a distance. Cream because it
 * is already the game's warm colour; the action red is reserved for "this starts a fight".
 */
const SPILL_COLOR = 0xf2e2c4;
const HALO_ALPHA = 0.22;
/** How much the halo breathes, as a fraction of its radius. */
/**
 * How much bigger a coin is drawn on a crest than in a trough, as a fraction. Bigger than the
 * hulls' LIFT, because a coin is a dot: two percent on a ship is legible and two percent on eleven
 * pixels is nothing at all.
 */
const RIDE_DEPTH = 0.22;
const PULSE_DEPTH = 0.16;
/** Pulse rate, in radians per second. */
const PULSE_RATE = 2.2;
/**
 * Phase shift per world pixel. Items pulse out of step with each other — a field of loot
 * all breathing in unison reads as one mechanism rather than as sixty floating things.
 */
const PULSE_SPREAD = 0.011;

/** How far off screen an item is still drawn, in pixels. Covers the halo at any position. */
const CULL_MARGIN = HALO_RADIUS * 2;

/**
 * How fast a drawn coin closes the gap to the server's version, per second. Exponential, so
 * it is a rate rather than a speed: at 18 a coin covers most of the gap within one patch,
 * which smooths the 20 Hz steps of a magnet pull without visibly trailing behind it.
 */
const FOLLOW_RATE = 18;

/**
 * Past this, a coin is snapped rather than eased, in world pixels. A coin only ever moves
 * under a magnet, and never faster than LOOT_MAGNET_SPEED_NEAR — so a jump bigger than this
 * is not movement at all, it is a client catching up after a stall, and easing through it
 * would draw a coin flying across the water.
 */
const SNAP_DISTANCE = 400;

/** One coin as it is drawn, which is a patch or so behind where the server says it is. */
interface DrawnCoin {
    x: number;
    y: number;
    /**
     * Pulse offset, fixed when the coin is first seen. Fixed rather than read off the live
     * position because a magnetised coin whose phase came from where it currently is would
     * visibly change its breathing rate as it moved.
     */
    phase: number;
}

/**
 * Every piece of loot in the sea, drawn as a plain white circle with a soft halo.
 *
 * It sits **above** the water rather than inside it. design/Water.md's rule is that things
 * loose in the sea ride the swell, but loot is a target the player steers at, and the
 * displacement filter's 80px warp would move it away from where it actually is. Legibility
 * wins, same as it does for hulls.
 *
 * A coin sits still until a hull's magnet takes hold of it, and then it is dragged in — by
 * the server, which owns collection and therefore owns the pull. What arrives here is 20 Hz
 * of that, so the drawn position **eases** toward the server's rather than snapping to it;
 * see design/Battle.md. A still coin is already on its target, so this costs the common case
 * nothing.
 */
export class LootField {
    private readonly _container = new Container();
    private readonly _coins = new Graphics();
    /** Where each coin is *drawn*, keyed by the server's loot id. See `_ease`. */
    private readonly _drawn = new Map<string, DrawnCoin>();
    /** Ids seen this frame, so coins that were taken can be dropped. Reused, never rebuilt. */
    private readonly _seen = new Set<string>();

    private _time = 0;
    private _width = 0;
    private _height = 0;

    public get container(): Container {
        return this._container;
    }

    constructor() {
        this._container.addChild(this._coins);
    }

    /**
     * Redraws the whole field against the camera. Cleared and rebuilt each frame, like the
     * minimap — at these counts it is cheaper than tracking one display object per item
     * through a map whose keys change every time somebody sails over a coin.
     */
    public update(
        loot: MapSchema<Loot> | null,
        sea: Sea,
        cameraX: number,
        cameraY: number,
        deltaSeconds: number
    ): void {
        this._time += deltaSeconds;
        this._coins.clear();

        if (!loot) {
            this._drawn.clear();
            return;
        }

        const centreX = this._width / 2;
        const centreY = this._height / 2;
        // Exponential, like the local boat's reconciliation: framerate-independent, so a coin
        // lands at the same moment on a 60 Hz machine and on a 144 Hz one.
        const follow = 1 - Math.exp(-FOLLOW_RATE * deltaSeconds);

        this._seen.clear();

        loot.forEach((item, id) => {
            this._seen.add(id);
            const coin = this._ease(id, item, follow);

            // wrapDelta, not subtraction: a coin just over the west edge belongs a few
            // pixels west of a camera on the east edge, not most of an arena away.
            const x = centreX + wrapDelta(cameraX, coin.x);
            const y = centreY + wrapDelta(cameraY, coin.y);

            if (x < -CULL_MARGIN || x > this._width + CULL_MARGIN) return;
            if (y < -CULL_MARGIN || y > this._height + CULL_MARGIN) return;

            const pulse = 1 + Math.sin(this._time * PULSE_RATE + coin.phase) * PULSE_DEPTH;
            const color = item.spilled ? SPILL_COLOR : COLOR;

            // The swell under it, as **size and nothing else**. A coin is a thing the player steers
            // at, so where it is drawn has to stay where it is (design/Water.md) — an offset would
            // have them sailing at the picture rather than at the pickup. Rising and falling in
            // place is the same reading and costs nothing: a field of coins breathing with the sea
            // is most of what makes the water look like it has something floating on it.
            const ride = 1 + waveHeightAt(sea, x - centreX, y - centreY) * WAVE_HEIGHT * RIDE_DEPTH;

            this._coins.circle(x, y, HALO_RADIUS * pulse * ride).fill({ color, alpha: HALO_ALPHA });
            this._coins.circle(x, y, RADIUS * ride).fill(color);
        });

        // Ids churn every time somebody sails over a coin, so the map is pruned rather than
        // left to grow for the length of the battle.
        for (const id of this._drawn.keys()) {
            if (!this._seen.has(id)) this._drawn.delete(id);
        }
    }

    /**
     * Moves one coin's drawn position toward the server's and returns it. A coin seen for the
     * first time is placed exactly where it is: easing it in from nowhere would have every
     * respawn slide across the water.
     *
     * Every item is eased, on screen or not — a coin culled while being dragged has to be in
     * the right place the moment it comes back into shot, not where it was last drawn.
     */
    private _ease(id: string, item: Loot, follow: number): DrawnCoin {
        const existing = this._drawn.get(id);

        if (!existing) {
            const fresh: DrawnCoin = { x: item.x, y: item.y, phase: (item.x + item.y) * PULSE_SPREAD };
            this._drawn.set(id, fresh);
            return fresh;
        }

        // Chased through the seam like everything else in the arena: a coin dragged over the
        // west edge is a few pixels from where it was drawn, not most of an arena away.
        const dx = wrapDelta(existing.x, item.x);
        const dy = wrapDelta(existing.y, item.y);

        if (Math.abs(dx) > SNAP_DISTANCE || Math.abs(dy) > SNAP_DISTANCE) {
            existing.x = item.x;
            existing.y = item.y;
            return existing;
        }

        existing.x = wrapCoord(existing.x + dx * follow);
        existing.y = wrapCoord(existing.y + dy * follow);
        return existing;
    }

    public resize(width: number, height: number): void {
        this._width = width;
        this._height = height;
    }

    public destroy(): void {
        this._drawn.clear();
        this._container.destroy({ children: true });
    }
}
